/** Log a weigh-in (and optional body-fat %) for the selected day. */
import { useState, type ReactElement } from 'react';
import { bodyFatFromLean } from '../lib/bodyComp';
import { timestampForDay } from '../lib/dates';
import { BODY_SOURCE_LABEL } from '../lib/labels';
import { errorMessage } from '../lib/logger';
import { BODY_STAT_SOURCES, type BodyStatSource } from '../lib/types';
import { massToKg, massUnit } from '../lib/units';
import { useApp } from '../state/app';
import { Icon } from './Icon';
import { Modal, Spinner } from './ui';

export function AddWeightModal({ day, onClose, onSaved }: { day: string; onClose: () => void; onSaved: () => void }): ReactElement {
  const { source, profile, today, toast } = useApp();
  const units = profile.unit_system;
  const [weight, setWeight] = useState<string>('');
  const [bodyFat, setBodyFat] = useState<string>('');
  const [lean, setLean] = useState<string>('');
  const [src, setSrc] = useState<BodyStatSource>('scale');
  const [fasted, setFasted] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setError(null);
    const w: number = Number(weight);
    if (!Number.isFinite(w) || w <= 0) { setError(`Enter your weight in ${massUnit(units)}.`); return; }
    const kg: number = Math.round(massToKg(w, units) * 100) / 100;
    if (kg < 20 || kg > 400) { setError('That weight is outside the allowed range (20–400 kg / 44–880 lb).'); return; }
    let bf: number | null = bodyFat.trim() === '' ? null : Number(bodyFat);
    if (bf !== null && (!Number.isFinite(bf) || bf < 2 || bf > 75)) { setError('Body fat % must be between 2 and 75.'); return; }
    let leanKg: number | null = null;
    if (lean.trim() !== '') {
      const l: number = Number(lean);
      leanKg = Number.isFinite(l) ? Math.round(massToKg(l, units) * 100) / 100 : Number.NaN;
      if (!Number.isFinite(leanKg) || leanKg < 10 || leanKg > 250 || leanKg >= kg) { setError('Lean mass must be less than your weight (and between 10 and 250 kg).'); return; }
      // Keep the pair consistent: a lean mass alone also stores the body-fat % it implies.
      if (bf === null) bf = Math.round(bodyFatFromLean(kg, leanKg) * 10) / 10;
    }
    setBusy(true);
    try {
      await source.insertBodyStat({
        measured_at: day === today ? new Date().toISOString() : timestampForDay(day, 7),
        local_date: day, weight_kg: kg, body_fat_pct: bf, lean_mass_kg: leanKg, source: src, is_fasted: fasted,
        notes: notes.trim() === '' ? null : notes.trim().slice(0, 500),
      });
      toast(`Logged ${w} ${massUnit(units)}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log weigh-in" onClose={onClose} width={520} footer={
      <>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? <Spinner /> : <Icon name="check" size={18} />} Save</button>
      </>
    }>
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <label className="field"><span>Weight</span>
            <div className="input-suffix"><input className="input" type="number" inputMode="decimal" step="0.1" autoFocus value={weight} onChange={(e) => setWeight(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} /><em>{massUnit(units)}</em></div>
          </label>
          <label className="field"><span>Body fat (optional)</span>
            <div className="input-suffix"><input className="input" type="number" inputMode="decimal" step="0.1" value={bodyFat} onChange={(e) => setBodyFat(e.target.value)} /><em>%</em></div>
          </label>
          <label className="field"><span>Lean mass (optional)</span>
            <div className="input-suffix"><input className="input" type="number" inputMode="decimal" step="0.1" value={lean} onChange={(e) => setLean(e.target.value)} /><em>{massUnit(units)}</em></div>
            <small>From a DEXA or smart-scale report.</small>
          </label>
          <label className="field"><span>Measured with</span>
            <select className="input" value={src} onChange={(e) => setSrc(e.target.value as BodyStatSource)}>
              {BODY_STAT_SOURCES.map((s) => <option key={s} value={s}>{BODY_SOURCE_LABEL[s]}</option>)}
            </select>
          </label>
          <label className="check" style={{ alignSelf: 'end', paddingBottom: 10 }}><input type="checkbox" checked={fasted} onChange={(e) => setFasted(e.target.checked)} /> Morning, before eating</label>
        </div>
        <label className="field"><span>Notes (optional)</span><input className="input" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. after a high-sodium dinner" /></label>
        <div className="note info">Tip: weigh first thing in the morning, after the bathroom, before food. The first weigh-in of each day is the one used for your trend.</div>
        {error !== null && <div className="note err">{error}</div>}
      </div>
    </Modal>
  );
}
